// 숨은 탕으로 가는 오솔길, 얼어붙은 개울과 자갈, 종이등(안돈), 울타리 / 길 입구 표지판, 대기 벤치. 프로토타입 520–582, 620–660.
// 장식이 쌓이는 순서가 결과에 영향을 주므로(합치기·장애물 순서) 프로토타입처럼 buildPaths → 대나무 → buildSigns 순으로 부른다.
import * as THREE from 'three';
import { S, mk } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { angDiff, rnd } from '../../engine/math';
import { deckWood, logWood, oldWood, rockMats, snowMat, stoneMat } from './materials';
import { snowRoof } from './buildings';
import type { QuietScene } from './scene';

const v3 = (x: number, z: number) => new THREE.Vector3(x, 0, z);
const pathMat = new THREE.MeshStandardMaterial({ color: 0xCAD4DC, roughness: 1 });
const andonGlow = new THREE.MeshStandardMaterial({ color: 0xFFF6E2, emissive: 0xFFE6B8, emissiveIntensity: 0.9, roughness: 1 });
const fencePole = plush('#C2A86C', '#E8D7A8', 0.04, true);

/** 곡선을 따라 폭이 살짝 일렁이는 띠 (길·개울). */
function strip(world: THREE.Object3D, curve: THREE.Curve<THREE.Vector3>, width: number, mat: THREE.Material, y: number): THREE.Mesh {
  const pts = curve.getSpacedPoints(90), pos: number[] = [], idx: number[] = [];
  pts.forEach((p, i) => {
    const q = pts[Math.min(i + 1, pts.length - 1)]!, o = pts[Math.max(i - 1, 0)]!;
    const tx = q.x - o.x, tz = q.z - o.z, l = Math.hypot(tx, tz) || 1, nx = -tz / l, nz = tx / l;
    const w = width * (0.85 + 0.15 * Math.sin(i * 0.7));
    pos.push(p.x + nx * w / 2, y, p.z + nz * w / 2, p.x - nx * w / 2, y, p.z - nz * w / 2);
    if (i < pts.length - 1) { const k = i * 2; idx.push(k, k + 2, k + 1, k + 1, k + 2, k + 3); }
  });
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(idx); g.computeVertexNormals();
  const m = new THREE.Mesh(g, mat); m.receiveShadow = true; world.add(m);
  return m;
}

export function buildPaths(Q: QuietScene): void {
  const { world, scenery, L } = Q;
  Q.paths = [
    new THREE.CatmullRomCurve3([v3(-6.2, -8.6), v3(-7.6, -10.4), v3(-10.2, -12.6), v3(-13.4, -15), v3(-16.2, -16.8), v3(-17.5, -17.6)]),
    new THREE.CatmullRomCurve3([v3(6.2, -8.6), v3(7.6, -10.4), v3(10.2, -12.6), v3(13.4, -15), v3(16.2, -16.8), v3(17.6, -17.7)]),
  ];
  Q.pathPts = Q.paths.flatMap((c) => c.getSpacedPoints(60));
  Q.paths.forEach((c) => strip(world, c, 1.7, pathMat, 0.012));

  // 숨은 개울 탕 뒤 얼어붙은 개울과 자갈 테두리
  const creek = new THREE.CatmullRomCurve3([v3(-30, -12), v3(-25.5, -17), v3(-22.5, -22.6), v3(-17, -25), v3(-11, -27)]);
  strip(world, creek, 1.8, new THREE.MeshStandardMaterial({ color: 0xCFE5EE, roughness: 0.25, metalness: 0.05 }), 0.02);
  Q.creekPts = creek.getSpacedPoints(50);
  Q.creekPts.forEach((p, i) => {
    if (i % 3) return;
    const side = i % 2 ? 1 : -1, sz = 0.3 + rnd(i + 4000) * 0.25;
    const r = mk(S(+sz.toFixed(2), 12, 8), rockMats[i % 4]!); r.scale.set(1.2, 0.6, 1); r.position.set(p.x + side * 1.1, sz * 0.2, p.z + (rnd(i + 4100) - 0.5)); scenery.add(r);
    const c = mk(S(+(sz * 0.85).toFixed(2), 12, 6), snowMat); c.scale.set(1.2, 0.4, 1); c.position.set(r.position.x, sz * 0.5, r.position.z); scenery.add(c);
  });
  {
    const P = L.pools[6]!, gravel = stoneMat('#9A9C98');
    for (let i = 0; i < 160; i++) {
      const a = rnd(i + 5100) * Math.PI * 2;
      if (Math.abs(angDiff(a, P.gaps[0]!)) < 0.35) continue;
      const R = P.rAt(a) + 0.6 + rnd(i + 5200) * 0.7, g = mk(S(0.07, 6, 4), gravel); g.scale.set(1, 0.6, 1); g.castShadow = false;
      g.position.set(P.x + Math.cos(a) * R, 0.02, P.z + Math.sin(a) * R); scenery.add(g);
    }
  }

  // 기둥 위 종이등
  function andon(x: number, z: number) {
    const post = mk(new THREE.BoxGeometry(0.08, 0.9, 0.08), oldWood); post.position.set(x, 0.45, z); scenery.add(post);
    const lamp = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.42, 0.3), andonGlow); lamp.position.set(x, 1.05, z); scenery.add(lamp);
    [-1, 1].forEach((a) => [-1, 1].forEach((b) => { const f = mk(new THREE.BoxGeometry(0.03, 0.44, 0.03), oldWood); f.position.set(x + a * 0.15, 1.05, z + b * 0.15); scenery.add(f); }));
    const cap = mk(new THREE.BoxGeometry(0.38, 0.05, 0.38), oldWood); cap.position.set(x, 1.28, z); scenery.add(cap);
    const sn = mk(new THREE.BoxGeometry(0.34, 0.08, 0.34), snowMat); sn.position.set(x, 1.33, z); scenery.add(sn);
    L.obstacles.push({ x, z, r: 0.3 });
  }
  Q.paths.forEach((c) => [0.12, 0.38, 0.64, 0.9].forEach((u, k) => {
    const p = c.getPointAt(u), q = c.getPointAt(Math.min(1, u + 0.02)), tx = q.x - p.x, tz = q.z - p.z, l = Math.hypot(tx, tz) || 1, side = k % 2 ? 1 : -1;
    andon(p.x - tz / l * 1.4 * side, p.z + tx / l * 1.4 * side);
  }));
  { const P = L.pools[6]!; [P.gaps[0]! + 1.4, P.gaps[0]! - 1.4, P.gaps[0]! + Math.PI].forEach((a) => { const R = P.rAt(a) + 1.6; andon(P.x + Math.cos(a) * R, P.z + Math.sin(a) * R); }); }
  ([[-4.6, -9.6], [4.6, -9.6], [-11.5, 4.2], [11.5, 4.2]] as const).forEach(([x, z]) => andon(x, z));

  // 개울 탕 뒤 낮은 나무 울타리
  {
    const P = L.pools[6]!, back = P.gaps[0]! + Math.PI, R0 = P.r + 2.4;
    for (let u = -1.0; u <= 1.0001; u += 0.25) {
      const a = back + u, x = P.x + Math.cos(a) * R0, z = P.z + Math.sin(a) * R0;
      const post = mk(new THREE.BoxGeometry(0.1, 0.9, 0.1), oldWood); post.position.set(x, 0.45, z); scenery.add(post); L.obstacles.push({ x, z, r: 0.25 });
      if (u < 0.99) {
        const a2 = a + 0.125, x2 = P.x + Math.cos(a2) * R0, z2 = P.z + Math.sin(a2) * R0;
        [0.45, 0.8].forEach((y) => { const rail = mk(new THREE.BoxGeometry(0.08, 0.07, R0 * 0.25), oldWood); rail.position.set(x2, y, z2); rail.rotation.y = -a2; scenery.add(rail); });
        const sn = mk(new THREE.BoxGeometry(0.16, 0.08, R0 * 0.25), snowMat); sn.position.set(x2, 0.87, z2); sn.rotation.y = -a2; scenery.add(sn);
      }
    }
  }
  // 통 탕 뒤 대나무 울타리
  {
    const P = L.pools[7]!, back = P.gaps[0]! + Math.PI, R0 = P.r + 1.9;
    for (let u = -1.0; u <= 1.0; u += 0.07) {
      const a = back + u, x = P.x + Math.cos(a) * R0, z = P.z + Math.sin(a) * R0;
      const pole = mk(new THREE.CylinderGeometry(0.035, 0.035, 1.5, 6), fencePole); pole.position.set(x, 0.75, z); scenery.add(pole);
      if (Math.abs(u % 0.5) < 0.07) L.obstacles.push({ x, z, r: 0.35 });
    }
    [0.4, 1.05].forEach((y) => {
      for (let u = -1.0; u < 1.0; u += 0.25) {
        const a = back + u + 0.125, x = P.x + Math.cos(a) * (R0 + 0.05), z = P.z + Math.sin(a) * (R0 + 0.05);
        const rail = mk(new THREE.CylinderGeometry(0.03, 0.03, R0 * 0.26, 6), fencePole); rail.rotation.z = Math.PI / 2; rail.rotation.y = -(a + Math.PI / 2); rail.position.set(x, y, z); scenery.add(rail);
      }
    });
  }
}

/** 지붕 달린 나무 표지판. 글자는 장면의 일부라 여기 둔다 (화면 UI 문구가 아님). */
function signboard(Q: QuietScene, x: number, z: number, faceAng: number, kanji: string, english: string) {
  const wood = plush('#8C6A48', '#B8936C', 0.04), roofMat = plush('#4E4A46', '#7A746E', 0.03);
  const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = faceAng; Q.world.add(g);
  [-0.5, 0.5].forEach((o) => { const post = mk(new THREE.BoxGeometry(0.09, 1.9, 0.09), wood); post.position.set(o, 0.95, 0); g.add(post); });
  const c = document.createElement('canvas'); c.width = 256; c.height = 400; const cx = c.getContext('2d')!;
  cx.fillStyle = '#D8BE94'; cx.fillRect(0, 0, 256, 400); cx.strokeStyle = '#9C7A54'; cx.lineWidth = 8; cx.strokeRect(6, 6, 244, 388);
  cx.fillStyle = '#2E2016'; cx.textAlign = 'center'; cx.textBaseline = 'middle';
  cx.font = '700 76px "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif CJK JP", serif';
  [...kanji].forEach((ch, i) => cx.fillText(ch, 128, 74 + i * 84));
  cx.font = '600 30px Fredoka, ui-rounded, system-ui, sans-serif'; cx.fillText(english, 128, 360);
  const tex = new THREE.CanvasTexture(c);
  const faceMat = new THREE.MeshStandardMaterial({ map: tex, roughness: 1 }), edge = plush('#B89670', '#B89670', 0);
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.78, 1.22, 0.06), [edge, edge, edge, edge, faceMat, faceMat]); board.position.y = 1.15; board.castShadow = true; g.add(board);
  [-1, 1].forEach((sd) => {
    const rf = mk(new THREE.BoxGeometry(0.62, 0.04, 0.5), roofMat); rf.position.set(sd * 0.28, 1.94, 0); rf.rotation.z = -sd * 0.42; g.add(rf);
    const sn = mk(new THREE.BoxGeometry(0.58, 0.05, 0.46), snowMat); sn.position.set(sd * 0.28, 1.98, 0); sn.rotation.z = -sd * 0.42; g.add(sn);
  });
  Q.L.obstacles.push({ x, z, r: 0.6 });
}

export function buildSigns(Q: QuietScene): void {
  const { scenery, L } = Q, BIG = L.big;
  ([[Q.paths[0]!, '沢の湯', 'Creek spring', -1], [Q.paths[1]!, '竹の湯', 'Bamboo spring', 1]] as const).forEach(([c, kj, en, sd]) => {
    const p = c.getPoint(0.03), q = c.getPoint(0.07), tx = q.x - p.x, tz = q.z - p.z, l = Math.hypot(tx, tz);
    const x = p.x - tz / l * 1.6 * sd, z = p.z + tx / l * 1.6 * sd;
    signboard(Q, x, z, Math.atan2(BIG.x - x, BIG.z - z), kj, en);
  });

  // 대기 벤치: 각 길 중간쯤 통나무 기둥 지붕 아래 나무 벤치. 숨은 탕이 차 있으면 여기서 기다린다.
  L.pools.filter((P) => P.name === 'hidden').forEach((P, k) => {
    const c = Q.paths[k]!, p = c.getPointAt(0.42), q = c.getPointAt(0.45);
    const tx = q.x - p.x, tz = q.z - p.z, l = Math.hypot(tx, tz) || 1, ux = tx / l, uz = tz / l, nx = -uz, nz = ux, side = k === 0 ? 1 : -1;
    const bx = p.x + nx * 2.1 * side, bz = p.z + nz * 2.1 * side, ang = Math.atan2(uz, ux);
    const g = new THREE.Group(); g.position.set(bx, 0, bz); g.rotation.y = -ang; scenery.add(g);
    const top = mk(new THREE.BoxGeometry(3.4, 0.1, 0.62), deckWood); top.position.y = 0.42; g.add(top);
    [-1.5, 1.5].forEach((o) => { const leg = mk(new THREE.BoxGeometry(0.12, 0.4, 0.5), deckWood); leg.position.set(o, 0.2, 0); g.add(leg); });
    ([[-1.9, -0.75], [1.9, -0.75], [-1.9, 0.55], [1.9, 0.55]] as const).forEach(([x, z]) => {
      const post = mk(new THREE.CylinderGeometry(0.09, 0.11, 2.3, 8), logWood); post.position.set(x, 1.15, z); g.add(post);
      const w = new THREE.Vector3(x, 0, z).applyAxisAngle(new THREE.Vector3(0, 1, 0), -ang); L.obstacles.push({ x: bx + w.x, z: bz + w.z, r: 0.25 });
    });
    const roofG = new THREE.Group(); roofG.position.z = -0.1; g.add(roofG);
    snowRoof(roofG, 4.4, 2.2, 2.3, 0.4, plush('#6B5A44', '#958368', 0.03, true));
    P.wait = [-0.85, 0.85].map((o) => ({ x: bx + ux * o, z: bz + uz * o, face: Math.atan2(-nx * side, -nz * side), occ: null, since: 0 }));
  });
}
